import * as yup from 'yup';
import type {SubmitHandler} from 'react-hook-form';
import type {ResetPasswordUserInput} from '@/_application/core/types';
import {Form} from '@/_application/core/components/ui/forms/form';
import {Button} from '@kidslabs/mario-ui-nextjs-react-core';
import {StateMachineProvider as StateMachineProviderBase, createStore, useStateMachine, GlobalState} from 'little-state-machine';
const StateMachineProvider = StateMachineProviderBase as React.FC<React.PropsWithChildren>;
import {useModalAction} from '@/_application/core/components/ui/modal/modal.context';
import PasswordInput from '@/_application/core/components/ui/forms/password-input';
import {useForgotPassword, useVerifyForgotPasswordToken, useResetPassword} from '@/framework/user';
import {useTranslation} from 'next-i18next';
import {ArrowPrevIcon} from '../../../../icons/arrow-prev';
import React from 'react';
import {isMobile} from 'react-device-detect';
import {standardizePhoneNumber} from '@/lib/phone-utils';
import {useAtom} from 'jotai';
import { otpRequestedTimeAtom } from '../../stores/atom';
import PhoneNumberForm from '../otp/phone-number-form';
import OtpCodeForm from '../otp/code-verify-form';

const passwordFormValidation = yup.object().shape({
    password: yup
        .string()
        .required('text-password-required')
        .notOneOf([''])
        .min(8, 'error-password-length')
        .matches(/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}/, 'error-password-invalid-format'),
});

function PasswordForm({
    onSubmit,
    isLoading,
    handlePrevStep,
}: Readonly<{
    onSubmit: SubmitHandler<Pick<ResetPasswordUserInput, 'password'>>;
    isLoading: boolean;
    handlePrevStep: () => void;
}>) {
    const {t} = useTranslation('common');
    return (
        <Form<Pick<ResetPasswordUserInput, 'password'>>
            onSubmit={onSubmit}
            useFormProps={{
                defaultValues: {password: ''},
            }}
            validationSchema={passwordFormValidation}>
            {({register, formState: {errors}}) => (
                <>
                    <div className="flex items-center justify-center p-4">{t('text-type-your-new-password')}</div>
                    <div className={`${isMobile ? 'p-4' : ''}`}>
                        <div className="font-bold text-primary">{t('text-your-new-password')}</div>
                        <PasswordInput label="" {...register('password')} error={t(errors?.password?.message!)} />
                        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <Button
                                aria-label="Quay lại bước trước"
                                onClick={handlePrevStep}
                                className="order-1 w-full bg-cyan-500! text-sm tracking-[0.2px] hover:bg-cyan-600!">
                                <ArrowPrevIcon className="w-5" />
                                {t('text-previous-step-btn')}
                            </Button>
                            <Button
                                aria-label="Xác nhận mật khẩu mới"
                                className="w-full bg-blue-kid text-sm tracking-[0.2px] hover:bg-primary sm:order-2"
                                loading={isLoading}
                                disabled={isLoading}>
                                {t('text-confirm-new-password')}
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </Form>
    );
}

function RenderFormSteps() {
    const {mutate: forgotPassword, isLoading} = useForgotPassword();
    const {mutate: verifyForgotPasswordToken, isLoading: verifying} = useVerifyForgotPasswordToken();
    const {mutate: resetPassword, isLoading: resetting} = useResetPassword();
    // use hook for getting form state and actions
    const {state, actions} = useStateMachine({updateFormState});
    const {t} = useTranslation('common');
    const [, setOtpRequestedTime] = useAtom(otpRequestedTimeAtom);
    async function onSendCodeSubmission({phone_number}: {phone_number: string}) {
        forgotPassword(phone_number).then(() => {});
        setOtpRequestedTime(new Date().getTime());
    }
    const resendOtp = () => {
        onSendCodeSubmission({phone_number: state?.email});
    };
    const passwordFormHandle: SubmitHandler<Pick<ResetPasswordUserInput, 'password'>> = async ({password}) => {
        try {
            await resetPassword({
                newPassword: password,
                otp: state.token,
                phoneNumber: state.email,
                secret: state.secret,
            });
        } catch (e) {}
    };

    async function tokenFormHandle(values: any) {
        try {
            await verifyForgotPasswordToken({otp: values.code, phoneNumber: state.email, secret: state.secret});
        } catch (e) {}
    }
    function backToPreviousStep(step: GlobalState['step']) {
        actions.updateFormState({
            step,
        });
    }

    return (
        <div>
            {state.step === 'Email' && (
                <div className={`${isMobile ? 'p-4' : ''}`}>
                    <p
                        className={`${isMobile ? 'pt-4' : ''} text-center text-sm leading-relaxed text-body md:text-base`}>
                        {t('forgot-password-helper')}
                    </p>
                    <div className="py-4 text-base font-bold text-primary">{t('phone-number')}</div>
                    <div className="flex items-center">
                        <PhoneNumberForm onSubmit={onSendCodeSubmission} isLoading={isLoading} view="login" />
                    </div>
                </div>
            )}
            {state.step === 'Token' && (
                <>
                    <p
                        className={`${
                            isMobile ? 'py-8' : ''
                        } text-center text-sm leading-relaxed text-body sm:mb-8 sm:mt-5 md:text-base `}>
                        {t('text-type-your-phone-number', {phone: standardizePhoneNumber(state?.email)})}
                    </p>

                    <OtpCodeForm
                        isLoading={verifying}
                        onSubmit={tokenFormHandle}
                        handleDismiss={() => backToPreviousStep('Email')}
                        resendOtp={resendOtp}
                    />
                </>
            )}
            {state.step === 'Password' && (
                <PasswordForm
                    onSubmit={passwordFormHandle}
                    isLoading={resetting}
                    handlePrevStep={() => backToPreviousStep('Token')}
                />
            )}
        </div>
    );
}
export const initialState: GlobalState = {
    step: 'Email',
    email: '',
    password: '',
    token: '',
    secret: '',
};
//@ts-ignore
createStore(initialState);

export const updateFormState = (
    state: typeof initialState,
    payload: {
        step: 'Email' | 'Token' | 'Password';
        [key: string]: string;
    },
) => {
    return {
        ...state,
        ...payload,
    };
};
export default function ForgotUserPassword() {
    const {t} = useTranslation('common');
    const {openModal} = useModalAction();

    return (
        <StateMachineProvider>
            <div
                className={`${
                    isMobile ? '' : 'justify-center px-5'
                } flex h-full min-h-screen w-screen flex-col  bg-primary-0 py-6  sm:p-8 md:h-auto md:min-h-0 md:max-w-[480px] md:rounded-xl`}>
                <div className="flex items-center justify-center text-xl font-bold text-primary">
                    {t('text-forgot-password')}
                </div>
                <RenderFormSteps />
                <div className="relative mb-7 mt-9 flex flex-col items-center justify-center text-sm text-heading sm:mb-8 sm:mt-11">
                    <hr className="w-full" />
                    <span className="absolute -top-2.5 start-2/4 -ms-4 bg-primary-0 px-2">{t('text-or')}</span>
                </div>
                <div className="text-center text-sm text-body sm:text-base">
                    {t('text-back-to')}{' '}
                    <button
                        aria-label="Đăng nhập"
                        onClick={() => openModal('LOGIN_VIEW')}
                        className="ms-1 font-semibold text-accent underline transition-colors duration-200 hover:text-KPZ_turquoise hover:no-underline focus:text-accent-hover focus:no-underline focus:outline-none">
                        {t('text-login')}
                    </button>
                </div>
            </div>
        </StateMachineProvider>
    );
}
